import React, { useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Camera, CreditCard } from "lucide-react";
import Modal from "@/components/Modal";
import { AUTH_CARD_CLASS } from "@/components/auth/authStyles";
import { readImageFileAsDataUrl } from "@/utils/imageFile";
import {
  buildSignupQuery,
  getSignupStudentCard,
  readSignupParams,
  setSignupStudentCard,
} from "./signupState";

/**
 * Signup step 4 — student card. Ports the RN `ScanStudentCardScreen`.
 * The picked image is downscaled, then kept as a data URL in signup state and
 * uploaded later during registration. The store is the source of truth, so the
 * preview rehydrates on re-entry.
 */
const StudentCardPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { search } = useLocation();
  const params = readSignupParams(search);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [studentCard, setStudentCard] = useState<string>(getSignupStudentCard);
  const [imageError, setImageError] = useState("");
  const [modalAlertVisible, setModalAlertVisible] = useState(false);
  const [modalAlertText, setModalAlertText] = useState("");

  const pickImage = async (file?: File) => {
    if (!file) return;
    const dataUrl = await readImageFileAsDataUrl(file);
    if (!dataUrl) return;
    setStudentCard(dataUrl);
    setImageError(
      setSignupStudentCard(dataUrl) ? "" : t("Could not cache this photo. It will still be uploaded when you register."),
    );
  };

  const goNext = () => {
    navigate(
      `/signup/hkid${buildSignupQuery({
        ...params,
        studentCard: studentCard ? "set" : "",
      })}`,
    );
  };

  const onPressNext = () => {
    if (!studentCard) {
      setModalAlertText(t("Please upload your student card"));
      return setModalAlertVisible(true);
    }
    goNext();
  };

  const onPressSkip = () => {
    setStudentCard("");
    setSignupStudentCard("");
    goNext();
  };

  return (
    <div className={AUTH_CARD_CLASS}>
      <h1 className="text-2xl font-extrabold text-[#333]">
        {t("Upload your university card")}
      </h1>
      <p className="mt-1 text-sm text-gray-500">
        {t("Uninfo will verify your university card in 24 hrs")}
      </p>

      <div className="mt-8 flex flex-col items-center">
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          className="flex h-40 w-40 items-center justify-center overflow-hidden rounded-full border-4 border-dashed border-[#ffa217] bg-[#fff8f0] transition hover:bg-[#fff3e0]"
        >
          {studentCard ? (
            <img src={studentCard} alt="Student card" className="h-full w-full object-cover" />
          ) : (
            <CreditCard size={80} className="text-[#ffa217]" />
          )}
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => pickImage(e.target.files?.[0])}
        />
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-[#ffa217] transition hover:text-[#e08f0e]"
        >
          <Camera size={16} />
          {studentCard ? t("Change photo") : t("Pick an image")}
        </button>
        {imageError && (
          <p className="mt-2 max-w-xs text-center text-sm font-medium text-red-500">
            {imageError}
          </p>
        )}
      </div>

      <div className="mt-8 space-y-3">
        <button
          type="button"
          onClick={onPressNext}
          className="w-full rounded-lg bg-[#ffa217] py-3 font-bold text-white transition hover:bg-[#e08f0e]"
        >
          {t("NEXT")}
        </button>
        <button
          type="button"
          onClick={() => navigate(`/signup/profile${buildSignupQuery({ ...params })}`)}
          className="w-full rounded-lg bg-red-500 py-3 font-bold text-white transition hover:bg-red-600"
        >
          {t("BACK")}
        </button>
        <button
          type="button"
          onClick={onPressSkip}
          className="w-full rounded-lg bg-gray-400 py-3 font-bold text-white transition hover:bg-gray-500"
        >
          {t("SKIP")}
        </button>
      </div>

      <Modal visible={modalAlertVisible} onClose={() => setModalAlertVisible(false)}>
        <div className="rounded-2xl bg-white p-6 text-center shadow-lg">
          <div className="mb-4 text-lg font-semibold text-[#333]">{modalAlertText}</div>
          <button
            type="button"
            onClick={() => setModalAlertVisible(false)}
            className="rounded-lg bg-[#ffa217] px-6 py-2.5 font-semibold text-white transition hover:opacity-90"
          >
            {t("OK")}
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default StudentCardPage;